import { DateTime } from "luxon";
import { useAppStore } from "../../../stores/RootStore";
import { DataRow, Tab } from "../../../stores/TabsStore";
import { useState } from "react";
import _ from "lodash";
import { ReportViewButton } from "./ReportViewButton";
import { Separator } from "../../../ui/Separator";
import { ScrollArea } from "../../../ui/ScrollArea/ScrollArea";
import { Spring, animated } from "react-spring";
import Timeline, {
  TimelineMarkers,
  TimelineHeaders,
  TodayMarker,
  CursorMarker,
  SidebarHeader,
  DateHeader
} from "../../../ui/CalendarTimeline";
import { t } from "i18next";
import { rgbColor } from "../../../enums/colors";


const AnimatedTimeline = animated(
  ({
    animatedVisibleTimeStart,
    animatedVisibleTimeEnd,
    visibleTimeStart,
    visibleTimeEnd,
    ...props
  }) => (
    <Timeline
      className="flex-none w-full"
      visibleTimeStart={animatedVisibleTimeStart}
      visibleTimeEnd={animatedVisibleTimeEnd}
      {...props}
    />
  )
);

let keys = {
    groupIdKey: "id",
    groupTitleKey: "title",
    groupRightTitleKey: "rightTitle",
    itemIdKey: "id",
    itemTitleKey: "title",
    itemDivTitleKey: "title",
    itemGroupKey: "group",
    itemTimeStartKey: "start",
    itemTimeEndKey: "end",
    groupLabelKey: "title"
};

export function ReportCalendar({tab}:{tab: Tab}) {
  const {equipments, persons} = useAppStore();
  let reportData = _.orderBy(tab?.options?.reportData as DataRow[], "at")

  const [visibleTime, setVisibleTime] = useState({
    start: reportData[0].dateShift.start_time.toMillis(), 
    end: reportData[reportData.length - 1].dateShift.end_time.toMillis()
  })
  const [period, setPeriod] = useState("day")


  let groups: any[] = []
  equipments && equipments!.filter(eq => tab.defaultValues?.equipments?.map(e => e.value).includes(eq.id)).map(e => {
    groups.push({
      id: `e_${e.id}`,
      title:`${e.attributes.inventory_number}, ${e.attributes.name} `,
      rightTitle: `${e.attributes.inventory_number}, ${e.attributes.name} `
    })
    groups.push({
      id: `p_${e.id}`,
      title:`${t('interactions')}`,
      rightTitle: `${t('interactions')}`
    })
  })

  let items: any[] = []

  reportData.map(i => {
    items.push({
      id: items.length + 1,
      group: `e_${i.equipment.id}`,
      canMove: false,
      dragging: false,
      title:  
        <div className="flex flex-col w-full h-full">
          <div style={{background: rgbColor.turned_off, width: '100%', height: `${i.availability.off.seconds / i.duration.total.seconds * 100}%`}}></div>
          <div style={{background: rgbColor.stopped, width: '100%', height: `${i.availability.stopped.seconds / i.duration.total.seconds * 100}%`}}></div>
          <div style={{background: rgbColor.worked, width: '100%', height: `${i.availability.worked.seconds / i.duration.total.seconds * 100}%`}}></div>
        </div>,
      start: i.dateShift.start_time.valueOf(),
      end: i.dateShift.end_time.valueOf(),
      className: `z-[44] ${(i.dateShift.start_day.weekday === 6 || i.dateShift.start_day.weekday === 0) ? 'item-weekend' : ''}`,
      itemProps: {
        'data-tip': ""
      }
    })
    i.interactions.map(iter => {
      let person = persons?.find(p => Number(p.id) === Number(iter.personnel_id))
      items.push({
        id: items.length + 1,
        group: `p_${i.equipment.id}`,
        canMove: false,
        dragging: false,
        title: `${person?.attributes.last_name} ${person?.attributes.first_name.charAt(0).toUpperCase()} ${person?.attributes.middle_name.charAt(0).toUpperCase()}`,
        start: iter.from_ts,
        end: iter.to_ts,
        className: `z-[44] ${(i.dateShift.start_day.weekday === 6 || i.dateShift.start_day.weekday === 0) ? 'item-weekend' : ''}`,
        itemProps: {
          'data-tip': ""
        }
      })
    })
  })

  items = items.sort((a, b) => b - a)

  // console.log(items)

  const  handleTimeChange = (visibleTimeStart: number, visibleTimeEnd: number, updateScrollCanvas: any) => {
    setVisibleTime({start: visibleTimeStart, end: visibleTimeEnd})
    let seconds = visibleTimeEnd - visibleTimeStart
    if(seconds > 2678401000) {
      return setPeriod("year")
    } else if(seconds > 86401000) {
      return setPeriod("month")
    } else if(seconds > 3601000) {
      return setPeriod("day")
    } else {
      return setPeriod("hour")
    }
  };
  
  const onPrevClick = () => {
    const zoom = visibleTime.end - visibleTime.start;
    setVisibleTime({start: visibleTime.start - zoom, end: visibleTime.end - zoom})
  };
  
  const onNextClick = () => {
    const zoom = visibleTime.end - visibleTime.start;
    setVisibleTime({start: visibleTime.start + zoom, end: visibleTime.end + zoom})
  };

  function handleChangePeriod(unit: string) {
    setPeriod(unit)
    let currentYear = DateTime.fromMillis(visibleTime.start).year === DateTime.local().year
    let start = DateTime.fromMillis(visibleTime.start)
    let end = DateTime.fromMillis(visibleTime.end)
    switch (unit) {
      case "year": setVisibleTime({
        start: start
        .startOf("year")
        .toMillis(), 
        end: end
        .startOf("year")
        .plus({years: 1})
        .toMillis()
      })
      break;
      case "month": setVisibleTime({
        start: (currentYear ? DateTime.local() : start)
        .startOf("month")
        .toMillis(), 
        end: (currentYear ? DateTime.local() : end)
        .startOf("month")
        .plus({months: 1})
        .toMillis()
      })
      break;
      case "day": setVisibleTime({
        start: (currentYear ? DateTime.local() : start)
        .startOf("day")
        .toMillis(), 
        end: (currentYear ? DateTime.local() : end)
        .startOf("day")
        .plus({days: 1})
        .toMillis()
      })
      break;
      case "hour": setVisibleTime({
        start: (currentYear ? DateTime.local() : start)
        .startOf("hour")
        .toMillis(), 
        end: (currentYear ? DateTime.local() : end)
        .startOf("hour")
        .plus({hours: 1})
        .toMillis()
      })
      break;
      default: setVisibleTime({
        start: DateTime.local()
        .startOf("day")
        .toMillis(), 
        end: DateTime.local()
        .startOf("day")
        .plus({days: 1})
        .toMillis()
      })
      break;
    }
  }

  return (
    <div className="w-full overflow-hidden text-sm">
      <div className="flex flex-none h-12 w-full items-center px-2 justify-between">
        <div className="flex gap-2">
          <ReportViewButton />
          {/* <EquipmentsFilters
            filtersState={filtersColumns}
            excludeWeekendState={excludeWeekendState}
            tab={tab}
            categories={categories}
          /> */}
        </div>
      </div>
      <Separator className="flex-none" />
      <ScrollArea className='w-full tex-sm'>
        <Spring
          to={{
            animatedVisibleTimeStart: visibleTime.start,
            animatedVisibleTimeEnd: visibleTime.end
          }}
        >
          {(value: any) => (
            <AnimatedTimeline
              groups={groups}
              items={items}
              keys={keys}
              sidebarWidth={240}
              // sidebarContent={<div>{t('equipment')}</div>}
              canMove={false}
              canResize={false}
              canResizeLeft={false}
              canResizeRight={false}
              dragging={false}
              canSelect
              itemsSorted
              itemTouchSendsClick={false}
              stackItems
              itemHeightRatio={0.75}
              // onCanvasClick={handleCanvasClick}
              // onCanvasDoubleClick={handleCanvasDoubleClick}
              // onCanvasContextMenu={handleCanvasContextMenu}
              // onItemClick={handleItemClick}
              // onItemSelect={handleItemSelect}
              // onItemContextMenu={handleItemContextMenu}
              // onItemMove={handleItemMove}
              // onItemResize={handleItemResize}
              // onItemDoubleClick={handleItemDoubleClick}
              buffer={1}
              onTimeChange={handleTimeChange}
              {...value}
              // moveResizeValidator={moveResizeValidator}
            >
            <TimelineHeaders>
              <SidebarHeader>
              {({ getRootProps }) => {
                return <div {...getRootProps()} className='flex px-2 items-end leading-8'>{t('equipment')}</div>
              }}
              </SidebarHeader>
              <DateHeader unit="primaryHeader" />
              <DateHeader />
            </TimelineHeaders>
              <TimelineMarkers>
                <CursorMarker />
              </TimelineMarkers>
            </AnimatedTimeline>
          )}
        </Spring>
      </ScrollArea>
    </div>
  );
}


function shiftColor(worked: number, stopped: number, turnedOff: number, duration: number) {

}